<template>
  <div>
    <div v-if="simulationDevices.length > 1">
      <v-expansion-panels
        multiple
        v-model="panelOpen"
      >
        <v-expansion-panel
          v-for="(device, i) in simulationDevices"
          :key="i"
        >
          <v-expansion-panel-header
            :color="device.deviceColor || 'lightgray'"
          >
            {{ device.deviceLabel || `Controller ${i + 1}` }}
          </v-expansion-panel-header>
          <v-expansion-panel-content
            eager
            :color="device.deviceColor || 'lightgray'"
          >
            <component
              ref="devices"
              :is="device.component"
              v-bind="device.props"
              :localStoragePrefix="`${simulationLabel}_${i}_`"
            ></component>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </div>
    <div v-else>
      <component
        v-for="(device, i) in simulationDevices"
        :key="i"
        ref="devices"
        :is="device.component"
        v-bind="device.props"
        :localStoragePrefix="`${simulationLabel}_${i}_`"
      ></component>
    </div>
  </div>
</template>

<script>
import state from '@/state';

export default {
  props: [
    'busConfigurations',
    'outputConfigurations',
    'simulationDevices',
    'simulationLabel',
  ],

  data: () => ({
    panelOpen: [],
    devices: [],
  }),
  
  created() {
    for (let i = 0; i < this.simulationDevices.length; i += 1) {
      this.panelOpen.push(i);
    }
  },

  async mounted() {
    state.runAsync(async () => {
      for (let i = 0; i < this.simulationDevices.length; i += 1) {
        this.devices.push({
          index: i,
          config: this.simulationDevices[i],
          ref: this.$refs.devices[i],
        });
      }
      for (const device of this.devices) {
        // console.log(device);
        await device.ref.prepareDevice();
        device.ref.bridge.on('uartDidTransmit', (index, data) => {
          this.handleUartDidTransmit(device, index, data);
        });
        device.ref.bridge.on('updateOutputValue', (index, type, value) => {
          this.handleUpdateOutputValue(device, index, type, value);
        });
        device.ref.bridge.on('updatePlatformCopyConfiguration', (configId, index, buffer) => {
          this.handleUpdatePlatformCopyConfiguration(device, configId, index, buffer);
        });
      }
      for (const device of this.devices) {
        device.ref.startDevice();
      }
    });
  },

  methods: {
    handleUartDidTransmit(device, uartIndex, uartData) {
      if (!this.busConfigurations) { // if no bus is specified, at least send echo
        const buffer = Buffer.alloc(1);
        buffer[0] = uartData;
        device.ref.bridge.sendUartData(uartIndex, buffer);
        return;
      }
      // find all configurations with uartIndex in `to` and `from`
      const configurations = this.busConfigurations.filter((config) => {
        return config.connections.some((connection) => {
          if (connection.from.deviceId === device.config.id
            && connection.from.uart === uartIndex
          ) {
            return true;
          } else if (connection.to.deviceId === device.config.id
            && connection.to.uart === uartIndex
          ) {
            return true;
          }
          return false;
        });
      });

      // find all terminals that are connected with this virtual bus. makes shure to have every device just ones
      const terminalCandidates = [];
      for (const config of configurations) {
        for (const connection of config.connections) {
          const terminals = [ connection.from, connection.to ].filter(terminal => {
            return ((!connection.suppressEcho) || (terminal.deviceId !== device.config.id) || (terminal.uart !== uartIndex));
          });
          for (const terminal of terminals) {
            if (!terminalCandidates.find(x => x.deviceId === terminal.deviceId && x.uart === terminal.uart)) {
              terminalCandidates.push(terminal);
            }
          }
        }
      }
      
      // send tx byte to all folund terminals
      const buffer = Buffer.alloc(1);
      buffer[0] = uartData;

      for (const terminal of terminalCandidates) {
        const rxDevice = this.devices.find(x => x.config.id === terminal.deviceId);
        if (rxDevice) {
          rxDevice.ref.bridge.sendUartData(terminal.uart, buffer);
        }
      }
    },
    handleUpdateOutputValue(device, outputIndex, outputType, outputValue) {
      if (this.outputConfigurations) {
        const connections = [];
        // find all connections from this devices output index to another device / input
        this.outputConfigurations.forEach((config) => {
          config.connections.forEach((connection) => {
            if (connection.from.deviceId === device.config.id && connection.from.index === outputIndex) {
              connections.push(connection);
            }
          });
        });
        for (const connection of connections) {
          const rxDevice = this.devices.find(x => x.config.id === connection.to.deviceId);
          if (rxDevice) {
            if (connection.to.configId != null && rxDevice.ref.bridge.setPlatformConfiguration) {
              const buffer = Buffer.alloc(4);
              buffer.writeInt32LE(outputValue);
              rxDevice.ref.bridge.setPlatformConfiguration(connection.to.configId, connection.to.index, buffer);
            } else if (rxDevice.ref.bridge.setInputValueForType) {
              rxDevice.ref.bridge.setInputValueForType(connection.to.index, outputType, outputValue);
            }
          }
        }
      }
    },
    handleUpdatePlatformCopyConfiguration(device, configId, index, buffer) {
      if (this.outputConfigurations) {
        const connections = [];
        // find all connections from this devices output index to another device / input
        this.outputConfigurations.forEach((config) => {
          config.connections.forEach((connection) => {
            if (connection.from.deviceId === device.config.id
              && connection.from.configId === configId
              && connection.from.index === index) {
              connections.push(connection);
            }
          });
        });
        for (const connection of connections) {
          const rxDevice = this.devices.find(x => x.config.id === connection.to.deviceId);
          if (rxDevice) {
            if (connection.to.configId != null && rxDevice.ref.bridge.setPlatformConfiguration) {
              rxDevice.ref.bridge.setPlatformConfiguration(connection.to.configId, connection.to.index, buffer);
            } else if (rxDevice.ref.bridge.setInputValueForType) {
              let outputValue = null;

              if (buffer.length === 1) {
                outputValue = buffer.readInt8(0);
              } else if (buffer.length === 2) {
                outputValue = buffer.readInt16LE(0);
              } else if (buffer.length === 4) {
                outputValue = buffer.readInt32LE(0);
              }
              if (outputValue != null) {
                rxDevice.ref.bridge.setInputValueForType(connection.to.index, connection.to.outputType, outputValue);
              } else {
                console.log('handleUpdatePlatformCopyConfiguration: Invalid buffer length', buffer);
              }
            }
          }
        }
      }
    },
  },


};
</script>

<style>

</style>
